import { PageLayout } from '@/shared/components/PageLayout'
import { Link } from 'react-router-dom'
import { PAGE_URLS } from '@/lib/constants'
import { content } from '@/lib/content'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Building,
  Users,
  Wine,
  UtensilsCrossed,
  TreePine,
  Clock,
  Wifi,
  Tv,
  Music,
  ArrowRight,
  CheckCircle2,
  Camera,
  Mail
} from 'lucide-react'

const facilityOverview = [
  {
    name: 'Main Hall',
    icon: Building,
    capacity: '240 guests',
    description: 'Our largest venue for weddings and major events',
  },
  {
    name: 'Lower Hall',
    icon: Users,
    capacity: '100 guests',
    description: 'Perfect for intimate gatherings',
  },
  {
    name: 'Outdoor Deck',
    icon: TreePine,
    capacity: 'Open air',
    description: 'Attached to Main Hall',
  },
  {
    name: 'Full Bar',
    icon: Wine,
    capacity: 'Public',
    description: 'Open daily except Mondays',
  },
  {
    name: 'Kitchen',
    icon: UtensilsCrossed,
    capacity: 'Full-service',
    description: 'Award-winning fish fry',
  },
]

const mainHallFeatures = [
  'Seats 240 guests comfortably',
  'Full-service bar included',
  'Stage for bands and entertainment',
  'Dance floor available',
  'Attached outdoor deck',
  'Wireless internet & flat screen TVs',
]

const lowerHallFeatures = [
  'Seats 100 guests comfortably',
  'Adjacent bar area',
  'Perfect for showers & meetings',
  'Celebrity Bartender nights available',
  'Catered and non-catered options',
]

export default function FacilitiesPage() {
  return (
    <PageLayout
      title={content.facilities.seo.title}
      description={content.facilities.seo.description}
    >
      {/* Hero Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pattern-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

        <div className="relative content-container text-center">
          <Badge variant="outline" className="mb-6 border-primary/50 bg-primary/10 text-primary px-4 py-1.5">
            <Building className="mr-2 h-3.5 w-3.5" />
            Our Venues
          </Badge>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            {content.facilities.heading}
          </h1>

          <Separator className="w-16 bg-primary mx-auto mb-6" />

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {content.facilities.intro.description}
          </p>
        </div>
      </section>

      {/* Quick Overview Grid */}
      <section className="py-12 bg-secondary/30">
        <div className="content-container">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {facilityOverview.map((facility) => (
              <Card
                key={facility.name}
                className="bg-card border-border hover:border-primary/50 transition-colors text-center p-4"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                  <facility.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-sm mb-1">{facility.name}</h3>
                <Badge variant="outline" className="text-xs border-primary/30 text-primary mb-2">
                  {facility.capacity}
                </Badge>
                <p className="text-xs text-muted-foreground hidden md:block">{facility.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Facility Sections */}
      <section className="section-padding">
        <div className="content-container space-y-24 lg:space-y-32">

          {/* Main Hall */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative group">
              <Card className="overflow-hidden border-border bg-card">
                <div className="relative h-72 md:h-96 overflow-hidden">
                  <img
                    src={content.facilities.mainHall.image}
                    alt="Main Hall"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                    <Users className="mr-1.5 h-3 w-3" />
                    240 Guests
                  </Badge>
                </div>
              </Card>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-primary/20 rounded-lg -z-10 hidden lg:block" />
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Building className="h-6 w-6 text-primary" />
                </div>
                <Badge variant="outline" className="border-primary/50 text-primary">
                  Featured Venue
                </Badge>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {content.facilities.mainHall.heading}
              </h2>

              <Separator className="w-12 bg-primary mb-6" />

              <div className="space-y-4 text-muted-foreground leading-relaxed mb-6">
                {content.facilities.mainHall.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {mainHallFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lower Hall */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="lg:order-2 relative group">
              <Card className="overflow-hidden border-border bg-card">
                <div className="relative h-72 md:h-96 overflow-hidden">
                  <img
                    src={content.facilities.lowerHall.image}
                    alt="Lower Hall"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                    <Users className="mr-1.5 h-3 w-3" />
                    100 Guests
                  </Badge>
                </div>
              </Card>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-primary/20 rounded-lg -z-10 hidden lg:block" />
            </div>

            <div className="lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <Badge variant="outline" className="border-primary/50 text-primary">
                  Intimate Setting
                </Badge>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {content.facilities.lowerHall.heading}
              </h2>

              <Separator className="w-12 bg-primary mb-6" />

              <div className="space-y-4 text-muted-foreground leading-relaxed mb-6">
                {content.facilities.lowerHall.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {lowerHallFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deck */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative group">
              <Card className="overflow-hidden border-border bg-card">
                <div className="relative h-72 md:h-80 overflow-hidden">
                  <img
                    src={content.facilities.deck.image}
                    alt="Outdoor Deck"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                    <TreePine className="mr-1.5 h-3 w-3" />
                    Outdoor
                  </Badge>
                </div>
              </Card>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <TreePine className="h-6 w-6 text-primary" />
                </div>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {content.facilities.deck.heading}
              </h2>

              <Separator className="w-12 bg-primary mb-6" />

              <p className="text-muted-foreground leading-relaxed">
                {content.facilities.deck.description}
              </p>
            </div>
          </div>

          {/* Bar */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="lg:order-2 relative group">
              <Card className="overflow-hidden border-border bg-card">
                <div className="relative h-72 md:h-80 overflow-hidden">
                  <img
                    src={content.facilities.bar.image}
                    alt="The Bar"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                    <Wine className="mr-1.5 h-3 w-3" />
                    Full Service
                  </Badge>
                </div>
              </Card>
            </div>

            <div className="lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Wine className="h-6 w-6 text-primary" />
                </div>
                <Badge variant="outline" className="border-primary/50 text-primary">
                  Open to Public
                </Badge>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {content.facilities.bar.heading}
              </h2>

              <Separator className="w-12 bg-primary mb-6" />

              {/* Hours Card */}
              <Card className="bg-secondary/50 border-primary/20 mb-6">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Daily Hours (Closed Mondays)</p>
                    <p className="text-xl font-bold text-primary">{content.facilities.bar.hours}</p>
                    <p className="text-xs text-muted-foreground">Extended hours on weekends</p>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4 text-muted-foreground leading-relaxed">
                {content.facilities.bar.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>

          {/* Kitchen */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative group">
              <Card className="overflow-hidden border-border bg-card">
                <div className="relative h-72 md:h-80 overflow-hidden">
                  <img
                    src={content.facilities.kitchen.image}
                    alt="Kitchen"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                    <UtensilsCrossed className="mr-1.5 h-3 w-3" />
                    Full Kitchen
                  </Badge>
                </div>
              </Card>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <UtensilsCrossed className="h-6 w-6 text-primary" />
                </div>
                <Badge variant="outline" className="border-primary/50 text-primary">
                  Award Winning
                </Badge>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {content.facilities.kitchen.heading}
              </h2>

              <Separator className="w-12 bg-primary mb-6" />

              <p className="text-muted-foreground leading-relaxed">
                {content.facilities.kitchen.description}
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-secondary/50" />
        <div className="absolute inset-0 pattern-bg opacity-50" />

        <div className="relative content-container">
          <Card className="max-w-4xl mx-auto bg-card border-border overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/3 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center warm-glow">
                    <Camera className="h-10 w-10 text-primary" />
                  </div>
                  <p className="text-primary font-semibold uppercase tracking-wider text-sm">
                    Explore More
                  </p>
                </div>
              </div>

              <div className="md:w-2/3 p-8 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  {content.facilities.footer.heading}
                </h2>

                <p className="text-muted-foreground leading-relaxed mb-6">
                  See the{' '}
                  <Link
                    to={PAGE_URLS.GALLERY}
                    className="text-primary hover:text-primary/80 underline underline-offset-4"
                  >
                    gallery
                  </Link>{' '}
                  to see what we've done in the past! We can also cater your event!
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={PAGE_URLS.GALLERY}>
                    <Button variant="outline" className="cursor-pointer">
                      <Camera className="mr-2 h-4 w-4" />
                      View Gallery
                    </Button>
                  </Link>
                  <Link to={PAGE_URLS.CONTACT}>
                    <Button className="cursor-pointer">
                      <Mail className="mr-2 h-4 w-4" />
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </PageLayout>
  )
}
